import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import clsx from "clsx";
import { wrapBareText } from "@web-monorepo/translation-safe-dom";
import classes from "./DDSTooltip.module.css";

export const Sides = ["top", "right", "bottom", "left"] as const;
export const Alignments = ["start", "center", "end"] as const;
export const Variants = ["themeInverted", "themeMatching"] as const;

type CSSPropertiesWithVars = React.CSSProperties & {
  "--tooltip-max-width"?: React.CSSProperties["maxWidth"];
};

type DDSTooltipProps = {
  /** The content to display in the tooltip */
  content: React.ReactNode;
  /** The element that triggers the tooltip */
  children: React.ReactElement;
  /** The name will get used for automated product events.
   * @see https://www.notion.so/classdojo/Automatic-Product-Events-for-Web-bfc580f10a914c3ba514e5ec20f8ef9e?pvs=4
   */
  ["data-name"]: string;
  /** The side of the element to show the tooltip */
  side?: (typeof Sides)[number];
  /** The alignment of the tooltip relative to the trigger */
  align?: (typeof Alignments)[number];
  /** The delay in ms before showing the tooltip */
  delayDuration?: number;
  /** Whether the tooltip should be disabled */
  disabled?: boolean;
  /** Opt in when this tooltip belongs to a modal, so its portal appears above the modal backdrop. */
  insideModal?: boolean;
  /** Maximum width in pixels, defaults to 180 */
  maxWidth?: React.CSSProperties["maxWidth"];
  /** Whether the tooltip should wrap the content, defaults to no-wrap */
  wrapMode?: "no-wrap" | "break-word";
  /** The visual variant of the tooltip, defaults to themeInverted */
  variant?: (typeof Variants)[number];
  /** Whether to show a directional arrow pointing at the trigger, defaults to false */
  showArrow?: boolean;
  /**
   * Shows the tooltip without a hover, for announcing a control the viewer hasn't found
   * yet. Leave unset for the ordinary hover/focus tooltip. Also the escape hatch from the
   * DDS-179 note below, since the caller alone decides when the tooltip may come back.
   */
  open?: boolean;
  /**
   * Called when the tooltip wants to open or close (hover, focus, Escape, outside pointer down,
   * scroll). Pair with `open` to also open it from elsewhere — e.g. on click, so the tooltip is
   * reachable on touch devices — while keeping the default dismiss behaviour.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Click handler for the tooltip surface, not the trigger — lets a viewer dismiss a nudge
   * by clicking the thing that's in their way. A prop rather than a handler inside
   * `content` because radix renders `content` twice (visibly, and again in a hidden
   * `role="tooltip"` span), which would duplicate it.
   */
  onContentClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** Marks this as a promo surface for impression logging. See ai/rules/promos.md. */
  ["data-promo-id"]?: string;
};

export const DDSTooltip = React.forwardRef<HTMLDivElement, DDSTooltipProps>(
  (
    {
      children,
      content,
      ["data-name"]: dataName,
      side = "top",
      align = "center",
      delayDuration = 0,
      disabled = false,
      insideModal,
      wrapMode = "no-wrap",
      maxWidth = 180,
      variant = "themeInverted",
      showArrow = false,
      open,
      onOpenChange,
      onContentClick,
      ["data-promo-id"]: dataPromoId,
    },
    ref,
  ) => {
    if (disabled) {
      return children;
    }

    const tooltipStyle: CSSPropertiesWithVars = {
      "--tooltip-max-width": `${typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth}`,
    };
    const variantClass = variant === "themeMatching" ? classes.themeMatching : classes.themeInverted;

    // As of 2025-12-04, the following is a known issue: if an element (e.g., button) has a tooltip,
    // and it's also a popover or dialog trigger, then when the popover or dialog is closed, focus is
    // programmatically returned to the trigger (which is good), and that causes the tooltip to
    // reappear (which is odd).
    // See https://linear.app/classdojo/issue/DDS-179/tooltip-reappears-when-its-trigger-doubles-as-a-popoverdialog-trigger.
    // Passing `open` is the way out of it (see the prop's doc); leaving it undefined keeps
    // Radix uncontrolled, so the hover/focus default is untouched for everyone else.
    return (
      <TooltipPrimitive.Root delayDuration={delayDuration} open={open} onOpenChange={onOpenChange}>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            ref={ref}
            side={side}
            align={align}
            sideOffset={5}
            collisionPadding={10}
            className={clsx(
              classes.tooltipContent,
              { [classes.insideModal]: insideModal },
              variantClass,
              wrapMode === "break-word" && classes.breakWord,
              onContentClick && classes.clickable,
            )}
            style={tooltipStyle}
            data-name={dataName}
            data-promo-id={dataPromoId}
            onClick={onContentClick}
          >
            {/* radix renders `content` twice — here and in a visually-hidden role="tooltip" span —
                and the arrow makes both child lists arrays, so bare text stays React-owned. */}
            {wrapBareText(content)}
            {showArrow && <TooltipPrimitive.Arrow className={classes.arrow} />}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    );
  },
);

export const DDSTooltipProvider = TooltipPrimitive.Provider;

DDSTooltip.displayName = "DDSTooltip";
